import React, { Component } from 'react'
import { PropTypes } from 'prop-types'
import Combokeys from 'combokeys'
import { Button, Scroller, Tooltip } from 'dbrain-web-ui-lib'
import uuidv4 from 'uuid/v4'
import { offset, debounce, getX, getY, setNoScroll } from 'Utils/common'

import IconZoomIn from 'Components/Task/types/images/fullscreen-zoomin.inline.svg'
import IconZoomOut from 'Components/Task/types/images/fullscreen-zoomout.inline.svg'
import IconFullscreen from 'Components/Task/types/images/fullscreen.inline.svg'
import IconFullscreenClose from 'Components/Task/types/images/fullscreen-close.inline.svg'

import './style'

const makePoints = (polyLines, width, height) => polyLines.map(point => (
	point && point.length === 2
		? [point[0] * width, point[1] * height]
		: ''
))

const normalizePoints = (pts) => {
	const p = pts.trim()

	return p[p.length - 1] === ','
		? p.slice(0, p.length - 1)
		: p
}

const middlePolygonPoint = (polygon, width, height, stroke) => {
	let minX = polygon[0][0]
	let maxX = polygon[0][0]
	let minY = polygon[0][1]
	let maxY = polygon[0][1]

	polygon.forEach((point) => {
		const x = point[0]
		const y = point[1]

		if (x < minX) minX = x
		if (x > maxX) maxX = x
		if (y < minY) minY = y
		if (y > maxY) maxY = y
	})

	return {
		x: ((minX + maxX) / 2) * width,
		y: (((minY + maxY) / 2) * height) + (stroke * 2),
	}
}

export default class Core extends Component {
	constructor(props) {
		super(props)

		this.state = {
			rects: this.props.rects || {},

			currentRectId: null,
			copyKeyPressed: false,
			isFullscreenTooltipShown: false,
			resultPolygons: [],
			resultPolygonsReady: false,
		}

		this.maxZoom = 2
		this.minZoom = 1
		this.zoomStep = 0.1

		this._onMouseMove = this._onMouseMove.bind(this)
		this._onMouseUp = this._onMouseUp.bind(this)
		this.combokeys = new Combokeys(document.documentElement)
	}

	componentWillMount() {
		this._onResize = debounce(this.setResolution.bind(this), 250)
		window.addEventListener('resize', this._onResize)
		document.addEventListener('mousemove', this._onMouseMove)
		document.addEventListener('mouseup', this._onMouseUp)
		document.addEventListener('touchmove', this._onMouseMove)
		document.addEventListener('touchend', this._onMouseUp)

		this.combokeys.bind('esc', () => { this.handleEscKeyPress() })
		this.combokeys.bind('alt', () => { this.handleCopyKeyPressed(true) }, 'keydown')
		this.combokeys.bind('alt', () => { this.handleCopyKeyPressed(false) }, 'keyup')
	}

	componentDidMount() {
		this.setResolution()
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.comparedResult !== null && nextProps.isNeedToShowTaskResults) {
			this.setState({ resultPolygons: [], resultPolygonsReady: false }, () => {
				this.renderResultRects(nextProps.comparedResult.result)
			})
		} else {
			this.setState({ resultPolygons: [], resultPolygonsReady: false })
		}

		const equal = JSON.stringify(Object.keys(nextProps.rects)) === JSON.stringify(Object.keys(this.props.rects))
		if (!equal) {
			this._mouseup = null

			this.setState({ rects: nextProps.rects }, () => {
				this.setResolution()
			})
		} else {
			this.setResolution()
		}
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this._onResize)
		document.removeEventListener('mousemove', this._onMouseMove)
		document.removeEventListener('mouseup', this._onMouseUp)
		document.removeEventListener('touchmove', this._onMouseMove)
		document.removeEventListener('touchend', this._onMouseUp)
	}

	setResolution() {
		const el = this.props.imageRef.current
		const width = el.offsetWidth
		const height = el.offsetHeight
		const { rects } = this.state

		Object.keys(rects).forEach((rectId) => {
			if (Object.prototype.hasOwnProperty.call(rects, rectId)) {
				const rect = rects[rectId]
				rect.x = rect.rx * width
				rect.y = rect.ry * height
				rect.width = rect.rwidth * width
				rect.height = rect.rheight * height
			}
		})

		this.setState({ rects })
	}

	handleEscKeyPress() {
		const { currentRectId, rects } = this.state

		delete rects[currentRectId]
		if (currentRectId) this.setState({ rects, currentRectId: null })
	}

	handleCopyKeyPressed(copyKeyPressed) {
		this.setState({ copyKeyPressed })
	}

	_onMouseMove(e) {
		if (this._mousemove) {
			this._mousemove(e)
		}
	}

	_onMouseUp(e) {
		if (this._mouseup) {
			this._mouseup(e)
		}
	}

	el() {
		return document.querySelector(`.${this.props.container}`)
	}

	cloneRect(rectId, e) {
		const { rects } = this.state
		const o = Object.assign({}, rects[rectId])
		const currentRectId = uuidv4()
		const copyKeyPressed = false

		e.persist()

		rects[currentRectId] = o

		this.setState({ rects, currentRectId, copyKeyPressed }, () => {
			this.moveRect(currentRectId, e)
		})
	}

	moveRect(rectId, e) {
		const { rects, copyKeyPressed } = this.state
		const { onRectsUpdate, label } = this.props
		const movingRect = rects[rectId]

		if (!label || movingRect.label !== label) {
			return
		}

		e.stopPropagation()
		e.preventDefault()

		this.props.mouseDownHandler(true)

		if (copyKeyPressed) {
			this.cloneRect(rectId, e)
			return
		}

		if (!movingRect.showControls) {
			this.switchRectControls(rectId, true)
		}

		setNoScroll(true)
		movingRect.showDelete = false
		const startX = getX(e)
		const startY = getY(e)
		const x0 = movingRect.x
		const y0 = movingRect.y

		this.setState({ currentRectId: rectId })

		this._mousemove = (moveEvent) => {
			moveEvent.preventDefault()
			moveEvent.stopPropagation()
			const x = getX(moveEvent)
			const y = getY(moveEvent)
			movingRect.x = (x0 + x) - startX
			movingRect.y = (y0 + y) - startY
			this._fixMovingRect(movingRect, false)
			rects[rectId] = movingRect
			this.setState({ rects })
		}

		this._mouseup = () => {
			this.setState({ currentRectId: null })
			movingRect.showDelete = true
			setNoScroll(false)
			this._mousemove = null
			onRectsUpdate(rects)
			this.props.mouseDownHandler(false)
		}
	}

	_fixRect(rect, fixSize) {
		rect.rx = rect.x / this.el().clientWidth
		rect.ry = rect.y / this.el().clientHeight
		if (fixSize) {
			rect.width = Math.max(this.props.minBoxSide, rect.width)
			rect.height = Math.max(this.props.minBoxSide, rect.height)
		}
		rect.rwidth = rect.width / this.el().clientWidth
		rect.rheight = rect.height / this.el().clientHeight
	}

	_fixMovingRect(rect, fixSize) {
		rect.x = Math.max(rect.x, 1)
		rect.x = Math.min(rect.x, this.el().clientWidth - rect.width - 1)
		rect.y = Math.max(rect.y, 1)
		rect.y = Math.min(rect.y, this.el().clientHeight - rect.height - 1)
		this._fixRect(rect, fixSize)
	}

	_fixResizingRect(rect, fixSize) {
		const right = Math.min(rect.x + rect.width, this.el().clientWidth - 1)
		const bottom = Math.min(rect.y + rect.height, this.el().clientHeight - 1)
		rect.x = Math.max(rect.x, 1)
		rect.width = right - rect.x
		rect.y = Math.max(rect.y, 1)
		rect.height = bottom - rect.y
		this._fixRect(rect, fixSize)
	}

	resizeRect(rectId, downEvent, resize) {
		const {
			onRectsUpdate,
			minBoxSide,
			label,
			rectStyle,
			idx,
		} = this.props
		const { rects } = this.state
		let rect = rects[rectId]

		this.props.mouseDownHandler(true)

		if (!label || (rect && rect.label !== label)) {
			return
		}

		downEvent.stopPropagation()
		downEvent.preventDefault()

		if (rect && !rect.showControls) {
			this.switchRectControls(rectId, true)
		}
		setNoScroll(true)

		const startX = getX(downEvent)
		const startY = getY(downEvent)

		if (!rectId) {
			const { top, left } = offset(this.el())
			rect = {
				label,
				style: rectStyle,
				showDelete: false,
				new: true,
				idx,
				x: startX - left,
				y: startY - top,
				width: 0,
				height: 0,
			}

			// eslint-disable-next-line
			rectId = uuidv4()

			this._fixResizingRect(rect, false)
			rects[rectId] = rect
			this.setState({ rects })
		}

		rect.showDelete = false
		const prevWidth = rect.width
		const prevHeight = rect.height
		const x0 = rect.x
		const y0 = rect.y
		const r = resize || {
			top: true,
			left: true,
			bottom: true,
			right: true,
		}

		this.setState({ currentRectId: rectId })

		this._mousemove = (moveEvent) => {
			moveEvent.preventDefault()
			moveEvent.stopPropagation()

			const { currentRectId } = this.state

			if (!currentRectId) {
				this.props.mouseDownHandler(false)
				return
			}

			this.props.mouseDownHandler(true)

			if (rect && !rect.showControls) {
				this.switchRectControls(rectId, true)
			}

			const x = getX(moveEvent)
			const y = getY(moveEvent)
			const _minBoxSide = rect.new ? 0 : minBoxSide

			if (r.left && (prevWidth + (startX - x) > _minBoxSide)) {
				rect.x = x0 - (startX - x)
				rect.width = Math.max(prevWidth + (startX - x), 0)
			}
			if (r.top && (prevHeight + (startY - y) > _minBoxSide)) {
				rect.y = y0 - (startY - y)
				rect.height = Math.max(prevHeight + (startY - y), 0)
			}
			if (r.right && ((x - startX) + prevWidth > _minBoxSide)) {
				rect.width = Math.max((x - startX) + prevWidth, 0)
			}
			if (r.bottom && ((y - startY) + prevHeight > _minBoxSide)) {
				rect.height = Math.max((y - startY) + prevHeight, 0)
			}

			this._fixResizingRect(rect, !rect.new)
			rects[rectId] = rect
			this.setState({ rects })
		}

		this._mouseup = () => {
			const currentRectId = null
			rect.showDelete = true
			rect.new = false
			rect.createdAt = +new Date()
			this._fixResizingRect(rect, true)
			this.setState({ rects, currentRectId })
			setNoScroll(false)
			this._mousemove = null
			onRectsUpdate(rects)
			this.props.mouseDownHandler(false)
		}
	}

	deleteRect(rectId, e) {
		if (e) {
			e.stopPropagation()
			e.preventDefault()
		}
		const { rects } = this.state
		const { onRectsUpdate } = this.props
		delete rects[rectId]
		this.setState({ rects })
		onRectsUpdate(rects)
		this.props.mouseDownHandler(false)
	}

	switchRectControls(rectId, isVisible) {
		const { rects } = this.state
		const { isNeedToShowTaskResults } = this.props

		if (isNeedToShowTaskResults) return

		rects[rectId].showControls = (typeof isVisible === 'undefined') ? !rects[rectId].showControls : isVisible
		if (rects[rectId].showControls) {
			Object.keys(rects).forEach((rId) => {
				if (Object.prototype.hasOwnProperty.call(rects, rId) && rId !== rectId) {
					rects[rId].showControls = false
				}
			})
		}
		this.setState({ rects })
	}

	undo(e) {
		const { rects } = this.state
		const currentRectId = null

		let newest = 0
		let newestUuid

		e.preventDefault()

		Object.keys(rects).forEach((i) => {
			const current = rects[i].createdAt

			if (current > newest) {
				newest = current
				newestUuid = i
			}
		})

		delete rects[newestUuid]

		this.props.onRectsUpdate(rects)
		this.setState({ rects, currentRectId })
	}

	clear(e) {
		const rects = {}
		const currentRectId = null

		e.preventDefault()

		this.setState({ rects, currentRectId })
		this.props.clearPaths()
	}

	handleSvgAreaMouseup() {
		this.props.mouseDownHandler(false)
	}

	buttonToggleFullscreenHover(isFullscreenTooltipShown) {
		this.setState({ isFullscreenTooltipShown })
	}

	handleZoomButtonClick(type, callback) {
		const { zoomRatioPercentage, changeZoomRatio } = this.props

		const zoomRatio = type === 'up'
			? zoomRatioPercentage + this.zoomStep
			: zoomRatioPercentage - this.zoomStep

		changeZoomRatio(parseFloat(zoomRatio.toFixed(1)), callback)
	}

	renderResultRects(result) {
		const { isNeedToShowTaskResults } = this.props

		if (!isNeedToShowTaskResults) {
			this.setState({ resultPolygons: [] })
		}

		const resultPolygons = []
		const clientWidth = this.svgarea.clientWidth || this.svgarea.parentNode.clientWidth
		const clientHeight = this.svgarea.clientHeight || this.svgarea.parentNode.clientHeight
		const resultTypes = Object.keys(result)
		const style = {
			fill: 'rgba(42, 193, 120, 0.5)',
			fontSize: '16',
			stroke: '#2AC178',
			strokeWidth: 2,
		}

		resultTypes.forEach((resType, num) => {
			const polygonsTypes = result[resType]

			if (polygonsTypes.length) {
				polygonsTypes.forEach((polygon) => {
					const p = makePoints(polygon, clientWidth, clientHeight).join(', ')
					const middle = middlePolygonPoint(polygon, clientWidth, clientHeight, style.strokeWidth)
					const group = (
						<g className="lower" key={uuidv4()}>
							<polygon
								points={normalizePoints(p)}
								style={style}
							/>
							<text
								x={middle.x}
								y={middle.y}
								fill={style.stroke}
								fontSize={style.fontSize}
							>
								<tspan textAnchor="middle">{num + 1}</tspan>
							</text>
						</g>
					)

					resultPolygons.push(group)
				})
			}
		})

		this.setState({ resultPolygons, resultPolygonsReady: true })
	}

	renderRectConstrols(rect, rectId) {
		const xResize = Math.min(10, rect.width / 10)
		const yResize = Math.min(10, rect.height / 10)
		const xyResize = Math.min(xResize, yResize)
		const delSize = Math.min(9, rect.width / 3, rect.height / 3)
		const crossRadius = delSize * (2 / 3)
		const crossLineSize = Math.sqrt((crossRadius * crossRadius) / 2)
		let del = ''
		if (rect.showDelete) {
			del = (
				<g>
					<circle
						className="rect_circle_delete"
						cx={(rect.x + (rect.width / 2))}
						cy={rect.y + (rect.height / 2)}
						r={delSize}
						style={{ fill: rect.style.stroke }}
					/>
					<line
						x1={(rect.x + (rect.width / 2)) - crossLineSize}
						y1={(rect.y + (rect.height / 2)) - crossLineSize}
						x2={(rect.x + (rect.width / 2)) + crossLineSize}
						y2={rect.y + (rect.height / 2) + crossLineSize}
						style={{ stroke: 'white', strokeWidth: 2 }}
					/>
					<line
						x1={rect.x + (rect.width / 2) + crossLineSize}
						y1={(rect.y + (rect.height / 2)) - crossLineSize}
						x2={(rect.x + (rect.width / 2)) - crossLineSize}
						y2={rect.y + (rect.height / 2) + crossLineSize}
						style={{ stroke: 'white', strokeWidth: 2 }}
					/>
					<circle
						className="rect_circle_delete transparent"
						onMouseDown={this.deleteRect.bind(this, rectId)}
						onTouchStart={this.deleteRect.bind(this, rectId)}
						cx={(rect.x + (rect.width / 2))}
						cy={rect.y + (rect.height / 2)}
						r={delSize}
					/>
				</g>
			)
		}
		if (rect.label !== this.props.label) {
			return (
				<g>
					{del}
				</g>
			)
		}
		return (
			<g>
				<rect
					onMouseDown={e => this.resizeRect(rectId, e, { top: true })}
					onTouchStart={e => this.resizeRect(rectId, e, { top: true })}
					x={rect.x}
					y={rect.y - yResize}
					width={rect.width}
					height={2 * yResize}
					className="n-resize transparent"
				/>
				<rect
					onMouseDown={e => this.resizeRect(rectId, e, { right: true })}
					onTouchStart={e => this.resizeRect(rectId, e, { right: true })}
					x={(rect.x + rect.width) - xResize}
					y={rect.y}
					width={2 * xResize}
					height={rect.height}
					className="e-resize transparent"
				/>
				<rect
					onMouseDown={e => this.resizeRect(rectId, e, { bottom: true })}
					onTouchStart={e => this.resizeRect(rectId, e, { bottom: true })}
					x={rect.x}
					y={(rect.y + rect.height) - yResize}
					width={rect.width}
					height={2 * yResize}
					className="s-resize transparent"
				/>
				<rect
					onMouseDown={e => this.resizeRect(rectId, e, { left: true })}
					onTouchStart={e => this.resizeRect(rectId, e, { left: true })}
					x={rect.x - xResize}
					y={rect.y}
					width={2 * xResize}
					height={rect.height}
					className="w-resize transparent"
				/>


				<circle
					onMouseDown={e => this.resizeRect(rectId, e, { top: true, left: true })}
					onTouchStart={e => this.resizeRect(rectId, e, { top: true, left: true })}
					className="nw-resize transparent"
					cx={rect.x}
					cy={rect.y}
					r={xyResize}
				/>
				<circle
					onMouseDown={e => this.resizeRect(rectId, e, { bottom: true, right: true })}
					onTouchStart={e => this.resizeRect(rectId, e, { bottom: true, right: true })}
					className="se-resize transparent"
					cx={rect.x + rect.width}
					cy={rect.y + rect.height}
					r={xyResize}
				/>
				<circle
					onMouseDown={e => this.resizeRect(rectId, e, { bottom: true, left: true })}
					onTouchStart={e => this.resizeRect(rectId, e, { bottom: true, left: true })}
					className="sw-resize transparent"
					cx={rect.x}
					cy={rect.y + rect.height}
					r={xyResize}
				/>

				{del}
			</g>
		)
	}

	renderRect(rect, key, rectId) {
		if (rect) {
			const { isMarkButtonInCaption } = this.props
			const current = rect.label === this.props.label
			const { strokeWidth, fontSize, stroke } = rect.style
			const groupClassName = []

			groupClassName.push(current ? 'current' : '')
			groupClassName.push('user')

			return (
				<g
					onMouseEnter={this.switchRectControls.bind(this, rectId, true)}
					onMouseLeave={this.switchRectControls.bind(this, rectId, false)}
					onMouseDown={this.moveRect.bind(this, rectId)}
					onTouchStart={this.moveRect.bind(this, rectId)}
					key={key}
					className={groupClassName.join(' ')}
				>
					<rect
						x={rect.x}
						y={rect.y}
						width={rect.width}
						height={rect.height}
						className="rect"
						style={rect.style}
					/>

					{
						isMarkButtonInCaption
							? ''
							: (
								<text
									x={rect.x + (rect.width / 2)}
									y={(rect.y - strokeWidth) + (rect.height / 2) + (fontSize / 2)}
									fill={stroke}
									fontSize={fontSize}
									style={{ display: rect.showDelete ? 'block' : 'none' }}
								>
									<tspan textAnchor="middle">{rect.idx}</tspan>
								</text>
							)
					}

					{rect.showControls ? this.renderRectConstrols(rect, rectId) : ''}
				</g>
			)
		}
		return ''
	}

	renderResultRectsElements() {
		const { resultPolygons, resultPolygonsReady } = this.state

		if (resultPolygons.length && resultPolygonsReady) {
			return resultPolygons.map(poly => poly)
		}

		return ''
	}

	render() {
		const {
			imageUrl,
			label,
			container,
			zoomRatioPercentage,
			isNeedToShowTaskResults,
		} = this.props

		const { rects } = this.state

		const rectsElements = []

		Object.keys(rects).forEach((id) => {
			if (rects[id].label !== label) {
				rectsElements.push(this.renderRect(rects[id], id, id))
			}
		})
		Object.keys(rects).forEach((id) => {
			if (rects[id].label === label && !rects[id].showControls) {
				rectsElements.push(this.renderRect(rects[id], id, id))
			}
		})
		Object.keys(rects).forEach((id) => {
			if (rects[id].label === label && rects[id].showControls) {
				rectsElements.push(this.renderRect(rects[id], id, id))
			}
		})

		const outerSize = {
			width: document.body.offsetWidth,
			height: document.body.offsetHeight,
		}

		return (
			<div
				className="core-middle"
				ref={this.props.coreMiddleRef}
			>
				<Button
					className="btn btn-dark-gray core-button-fullscreenClose"
					onClick={() => {
						this.props.handleButtonToggleFullscreen(1, this.setResolution.bind(this))
					}}
				>
					<IconFullscreenClose />
				</Button>

				<div className="core-zoom">
					<Button
						className="btn btn-dark-gray core-button-zoom-in"
						onClick={() => { this.handleZoomButtonClick('up', this.setResolution.bind(this)) }}
						disabled={zoomRatioPercentage >= this.maxZoom}
					>
						<IconZoomIn />
					</Button>

					<Button
						className="btn btn-dark-gray core-button-zoom-out"
						onClick={() => { this.handleZoomButtonClick('down', this.setResolution.bind(this)) }}
						disabled={zoomRatioPercentage <= this.minZoom}
					>
						<IconZoomOut />
					</Button>

					<span className="core-zoom-percentage">
						{`${parseInt(zoomRatioPercentage * 100, 10)}%`}
					</span>
				</div>

				<div className="core-inner">
					<Scroller
						disabled={!this.props.isFullscreen}
						outerSize={outerSize}
						innerSize={this.props.innerFullscreenSize}
						className="core-scroller"
						fullscreenOffsetLeft={this.props.fullscreenOffsetLeft}
						fullscreenOffsetTop={this.props.fullscreenOffsetTop}
						scrollHeight={8}
					>
						<div
							className={container}
							ref={this.props.coreref}
							style={this.props.imageFullscreenSize}
						>
							<img
								src={imageUrl}
								alt=""
								ref={this.props.imageRef}
								className="image-value"
							/>
							<svg
								className="svg-area"
								ref={(c) => { this.svgarea = c }}
								onMouseDown={this.resizeRect.bind(this, null)}
								onMouseUp={this.handleSvgAreaMouseup.bind(this)}
								onTouchStart={this.resizeRect.bind(this, null)}
							>
								{this.renderResultRectsElements()}
								{rectsElements}
							</svg>

							<div className="svg-button-fullscreen">
								<Tooltip
									text="Fullscreen"
									position="bottom-right"
									show={this.state.isFullscreenTooltipShown}
								>
									<span
										tabIndex="0"
										role="button"
										onClick={() => {
											this.props.handleButtonToggleFullscreen(1, this.setResolution.bind(this))
										}}
										onMouseEnter={() => { this.buttonToggleFullscreenHover(true) }}
										onMouseLeave={() => { this.buttonToggleFullscreenHover(false) }}
									>
										<IconFullscreen />
									</span>
								</Tooltip>
							</div>
						</div>
					</Scroller>

					<div className="svg-bottom">
						<div className="links">
							<Button
								className="link"
								disabled={isNeedToShowTaskResults}
								onClick={e => this.undo(e)}
							>
								Undo
							</Button>
							<Button
								className="link"
								disabled={isNeedToShowTaskResults}
								onClick={e => this.clear(e)}
							>
								Clear all
							</Button>
						</div>
					</div>
				</div>
			</div>
		)
	}
}

Core.defaultProps = {
	imageUrl: null,
	rects: {},
	label: null,
	idx: null,
	coreref: null,
	clearPaths: null,
	isFullscreen: false,
	comparedResult: {},
}

Core.propTypes = {
	coreref: PropTypes.func,
	container: PropTypes.string.isRequired,
	minBoxSide: PropTypes.number.isRequired,
	imageUrl: PropTypes.string,
	rects: PropTypes.object,
	label: PropTypes.string,
	idx: PropTypes.number,
	rectStyle: PropTypes.object.isRequired,
	onRectsUpdate: PropTypes.func.isRequired,
	clearPaths: PropTypes.func,
	imageRef: PropTypes.object.isRequired,
	coreMiddleRef: PropTypes.object.isRequired,
	mouseDownHandler: PropTypes.func.isRequired,
	innerFullscreenSize: PropTypes.object.isRequired,
	handleButtonToggleFullscreen: PropTypes.func.isRequired,
	isFullscreen: PropTypes.bool,
	imageFullscreenSize: PropTypes.object.isRequired,
	fullscreenOffsetLeft: PropTypes.number.isRequired,
	fullscreenOffsetTop: PropTypes.number.isRequired,
	zoomRatioPercentage: PropTypes.number.isRequired,
	changeZoomRatio: PropTypes.func.isRequired,
	isMarkButtonInCaption: PropTypes.bool.isRequired,
	comparedResult: PropTypes.object,
	isNeedToShowTaskResults: PropTypes.bool.isRequired,
}
